import styled from 'styled-components';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicInput, palette, Radio, SimplePopover, Typography } from '@dev/ee-components';
import { filter, find, map } from 'lodash-es';
import { useSelector } from 'react-redux';

import { useMobile } from '@/services/hooks/useMobile';
import { getContactListsNames, getSelectedList } from '@/store/lists/actions';

import Props, { ContactsType } from './AddToListProps';
import { isPaidSubscriptionList } from '../../helperFunctions';

const DialogContainer = styled.div`
    input {
        padding: 0;
        border: 0; //prevent 2018 styles
    }
    .MuiCheckbox-root {
        padding: 4px 9px;
    }
`;

const NewListContainer = styled.div`
    &.create label:nth-of-type(2),
    &.existing label:nth-of-type(1) {
        border: 1px solid ${palette.indigo[6]};
        background: ${palette.indigo[0]};
        box-shadow: 1px 1px 4px -1px ${palette.gray[8]};
    }
    & [role='radiogroup'] {
        flex-direction: row;
        flex-wrap: initial;
    }
    label {
        background: ${palette.basic.paper};
        display: flex;
        justify-content: space-between;
        width: 50%;
        padding: 30px;
        border: 1px solid ${palette.gray[3]};
        border-radius: 4px;
        margin-left: 0;
        margin-right: 15px;
        box-sizing: border-box;
    }
    label + label {
        margin-right: 0;
        margin-left: 15px;
    }
`;

const InputContainer = styled.div`
    display: inline-block;
    width: 100%;
    box-sizing: border-box;
    margin-top: 1.2rem;
    margin-bottom: 1.6px;
    & > div > div:first-of-type,
    & > div > div:first-of-type > div,
    & .MuiButton-contained {
        width: 100%;
    }
`;

const ContactsTypeRadiosContainer = styled.div`
    margin-top: 10px;
    & [role='radiogroup'] {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
    }
    label {
        display: flex;
        justify-content: space-between;
        width: 47%;
    }
`;

export const AddContactsToList = (props: Props): JSX.Element => {
    const { onChange, onChangeContactsType, moveContacts, isContactsOptions } = props;
    const { t } = useTranslation();
    const [isNewList, setIsNewList] = useState(props.isNewList);
    const [contactsType, setContactsType] = useState(ContactsType.Openers);
    const [isDuplicatedListName, setIsDuplicatedName] = useState(false);
    const isMobile = useMobile();
    const contactsListNameCollection = useSelector(getContactListsNames);
    const selectedList = useSelector(getSelectedList);

    const filteredContactLists = filter(contactsListNameCollection, (item) => !isPaidSubscriptionList(item));
    const mappedContactLists = map(filteredContactLists, (item) => ({
        value: item.ListName,
        label: item.ListName,
    }));

    const handleRadioChange = (e): void => {
        const newValue = (e.target as HTMLInputElement).value;
        onChange(newValue === 'existing' ? false : true, t('newlist'));

        setIsNewList(newValue === 'existing' ? false : true);
    };

    const handleContactsTypeRadioChange = (e): void => {
        const contactsType = e.target.value;
        onChangeContactsType(contactsType);

        setContactsType(contactsType);
    };

    return (
        <DialogContainer>
            {!moveContacts && !isMobile && (
                <NewListContainer className={isNewList ? 'create' : 'existing'}>
                    <Radio
                        id="choose-list-type-radios"
                        name={'InputName'}
                        value={isNewList ? 'create' : 'existing'}
                        radios={[
                            {
                                id: 'add-to-exisiting-list',
                                label: t('existinglist'),
                                value: 'existing',
                            },
                            {
                                id: 'add-to-new-list',
                                label: t('newlist'),
                                value: 'create',
                            },
                        ]}
                        onChange={handleRadioChange}
                        labelPlacement={'start'}
                    />
                </NewListContainer>
            )}

            {!isNewList && (
                <div>
                    {moveContacts && <Typography variant="caption">{t('movecontactdesc')}</Typography>}
                    <InputContainer>
                        <SimplePopover
                            id="select-list-name"
                            options={mappedContactLists}
                            type="input"
                            label={t('selectlistname')}
                            value={selectedList ? selectedList?.ListName : null}
                            onItemClick={(e, obj): void => {
                                onChange(false, obj.value);
                            }}
                            listItemWidth="min-content"
                            listItemMaxLines={2}
                            maxHeight={200}
                        />
                    </InputContainer>
                </div>
            )}
            {isNewList && (
                <InputContainer>
                    <BasicInput
                        name="selectlistname"
                        label={t('selectlistname')}
                        placeholder={t('listname')}
                        id="new-list-name-input"
                        type={'text'}
                        error={isDuplicatedListName}
                        errorMessage={t('duplicatedlistname')}
                        defaultValue={t('newlist')}
                        onChange={(e): void => {
                            const inputTextExistsAsList = find(
                                mappedContactLists,
                                (item): boolean => item.value.trim() === e.currentTarget.value.trim()
                            );
                            if (!!inputTextExistsAsList !== isDuplicatedListName) {
                                setIsDuplicatedName(!!inputTextExistsAsList);
                            }
                            onChange(true, e.currentTarget.value);
                        }}
                    />
                </InputContainer>
            )}
            {isContactsOptions && (
                <ContactsTypeRadiosContainer>
                    <Radio
                        id="choose-contacts-type-radios"
                        name={'InputName'}
                        value={contactsType}
                        radios={[
                            {
                                id: 'openers',
                                label: t('openers'),
                                value: ContactsType.Openers,
                            },
                            {
                                id: 'non-openers',
                                label: t('nonopeners'),
                                value: ContactsType.Nonopeners,
                            },
                            {
                                id: 'clickers',
                                label: t('clickers'),
                                value: ContactsType.Clickers,
                            },
                            {
                                id: 'opened-not-clicked',
                                label: t('openednotclicked'),
                                value: ContactsType.OpenedNotClicked,
                            },
                        ]}
                        onChange={handleContactsTypeRadioChange}
                        labelPlacement={'end'}
                    />
                </ContactsTypeRadiosContainer>
            )}
        </DialogContainer>
    );
};
